/**
 * AppSidebar — sidebar desktop, 240px, 8 voci §9.4 in ordine fisso.
 *
 * Visibile solo da `md:` in su (≥768px). Sotto questa breakpoint la
 * navigazione passa al `BottomTabBar`.
 *
 * Stato attivo: sfondo `magenta-soft` + testo+icona `primary` (vedi
 * design.md §3.1).
 *
 * Footer sticky: avatar + nome + Badge ruolo + bottone logout
 * (touch target ≥ 44×44 per accessibilità anche se è desktop-only).
 *
 * Logo in alto: button semantico che esegue logout immediato
 * (confirmLogout, bypass dialog). Footer e UserMenuDropdown restano
 * con conferma. Idiom condiviso: signOut + navigate('/login').
 */

import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '../../hooks/useAuth';
import { useLogoutConfirm } from '../../hooks/useLogoutConfirm';
import { Avatar } from '../ui/Avatar';
import { Badge } from '@shared/components/ui/Badge';
import { Icon } from '@shared/components/ui/Icon';
import { roleBadgeTone, roleLabel } from '../../lib/role-display';
import { filterNavItemsForRole, SIDEBAR_NAV } from '../../lib/nav-items';
import { LogoutConfirmDialog } from './LogoutConfirmDialog';

export function AppSidebar() {
  const { user, role } = useAuth();
  const { confirmOpen, triggerLogout, confirmLogout, cancelLogout } =
    useLogoutConfirm();
  const navItems = filterNavItemsForRole(SIDEBAR_NAV, role);

  return (
    <aside className="app-sidebar hidden md:flex flex-col w-60 flex-shrink-0 bg-white border-r border-border-gray h-screen sticky top-0">
      {/* Logo + brand — logout immediato (bypass dialog; footer resta con conferma) */}
      <button
        type="button"
        onClick={() => void confirmLogout()}
        className="flex items-center gap-3 px-4 h-16 border-b border-border-gray w-full text-left hover:bg-bg-app transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-magenta-soft"
      >
        <div
          aria-hidden
          className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 40 40"
            aria-hidden="true"
          >
            <path
              d="M5 20C5 20 10 15 20 20C30 25 35 20 35 20"
              stroke="white"
              strokeLinecap="round"
              strokeWidth="3"
            />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="font-headline-md text-body-md font-bold text-text-main leading-tight truncate">
            Stucchi
          </p>
          <p className="text-[11px] uppercase tracking-wide text-text-secondary leading-tight">
            Studio Dietetico
          </p>
        </div>
      </button>

      {/* Voci di navigazione */}
      <nav
        aria-label="Navigazione principale"
        className="flex-1 overflow-y-auto py-4 px-2"
      >
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 h-11 px-3 rounded-lg font-body-md font-semibold transition-colors',
                    isActive
                      ? 'bg-magenta-soft text-primary'
                      : 'text-text-secondary hover:bg-bg-app hover:text-text-main'
                  )
                }
              >
                <Icon name={item.icon} size={20} />
                <span className="truncate">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer utente + logout */}
      <div className="border-t border-border-gray p-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            name={user?.displayName}
            email={user?.email}
            size="md"
            className="flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="font-body-sm font-semibold text-text-main truncate">
              {user?.displayName ?? user?.email ?? 'Utente'}
            </p>
            <div className="mt-0.5">
              <Badge tone={roleBadgeTone(role)}>{roleLabel(role)}</Badge>
            </div>
          </div>
          <button
            type="button"
            onClick={triggerLogout}
            aria-label="Esci"
            title="Esci"
            className="flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center text-text-secondary hover:bg-bg-app hover:text-text-main transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-magenta-soft"
          >
            <Icon name="logout" size={20} />
          </button>
        </div>
      </div>
      <LogoutConfirmDialog
        open={confirmOpen}
        onConfirm={() => void confirmLogout()}
        onCancel={cancelLogout}
      />
    </aside>
  );
}
